// Каналы гостя: чем гость может до вас достучаться и что с этим можно сделать.
//
// Карточка врала. Она считала канал подключённым по наличию строки в `channel_configs` -
// а строки там нет ровно у тех, кто пользуется общим ботом Smena, то есть у всех по
// умолчанию. Отсюда три следствия, каждое из которых видел клиент:
//
//   - рабочий MAX-бот значился «Не подключён» и тут же, строкой ниже, показывал своё имя;
//   - веб-чат значился «Не подключён», хотя он встроен в платформу и работает всегда,
//     без настроек и вне зависимости от тарифа;
//   - кнопки «Отвязать» не было нигде, потому что она рисовалась только при наличии строки.
//
// Правильная модель: канал работает, если на контуре поднят бот (или это веб-чат), и НЕ
// выключен явно в настройках аккаунта. Строка в channel_configs говорит только о том, чей
// бот - наш общий или свой whitelabel.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  PlugsConnectedIcon,
  CheckCircleIcon,
  ProhibitIcon,
  WarningIcon,
  LockSimpleIcon,
} from '@phosphor-icons/react';
import { ApiError, Button, Card, CardContent, CardHeader, CardTitle, StatusChip, type Tone } from '@smena/ui';
import {
  deleteChannel,
  fetchChannels,
  setChannelEnabled,
  type ChannelConfigView,
  type ChannelName,
} from './api';
import { fetchIntegrationsHealth, type ChannelStatus } from '@/features/integrations/api';

const CHANNELS: Array<{ name: ChannelName; title: string; hint: string }> = [
  { name: 'telegram', title: 'Telegram', hint: 'Гость приходит по ссылке из приглашения.' },
  { name: 'max', title: 'MAX', hint: 'Гость находит бота по имени и отправляет код брони.' },
  { name: 'vk', title: 'VK', hint: 'Гость пишет в сообщество первым сообщением с кодом брони.' },
  {
    name: 'webchat',
    title: 'Веб-чат',
    hint: 'Встроен в платформу: ссылка открывается в браузере, ничего устанавливать не нужно.',
  },
];

function readiness(statuses: ChannelStatus[] | undefined, name: ChannelName): ChannelStatus | undefined {
  const wanted = name === 'telegram' ? 'telegram_guest' : name;
  return statuses?.find((s) => s.kind === wanted);
}

const BOT_URL_BASE: Partial<Record<ChannelName, string>> = {
  telegram: 'https://t.me',
  max: 'https://max.ru',
};

interface BotLine {
  title: string;
  username?: string;
  url?: string;
}

/**
 * Кто принимает гостей в этом канале. Свой бот УК берём из настройки аккаунта, общий -
 * из API мессенджера: на каждом контуре он свой, и env про него врёт.
 */
function botOf(
  name: ChannelName,
  cfg: ChannelConfigView | undefined,
  stand: ChannelStatus | undefined,
): BotLine | null {
  const base = BOT_URL_BASE[name];
  if (cfg?.kind === 'custom') {
    const username = cfg.botUsername ?? undefined;
    return {
      title: username ? `@${username}` : 'ваш бот',
      ...(username && base ? { url: `${base}/${username}` } : {}),
    };
  }
  if (!stand?.botName) return null;
  return {
    title: stand.botName,
    ...(stand.botUsername ? { username: stand.botUsername } : {}),
    ...(stand.botUrl ? { url: stand.botUrl } : {}),
  };
}

function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

type State = 'works' | 'off' | 'unavailable';

/**
 * Состояние канала для аккаунта.
 *
 * `unavailable` - это про контур, а не про клиента: бота на стенде нет, и подключать нечего.
 * Веб-чат сюда попасть не может - он не зависит от внешних сервисов.
 */
function stateOf(name: ChannelName, cfg: ChannelConfigView | undefined, stand: ChannelStatus | undefined): State {
  if (cfg?.status === 'inactive') return 'off';
  if (name === 'webchat') return 'works';
  return cfg?.kind === 'custom' || stand?.configured ? 'works' : 'unavailable';
}

const STATE_CHIP: Record<State, { tone: Tone; label: string }> = {
  works: { tone: 'green', label: 'работает' },
  off: { tone: 'muted', label: 'выключен' },
  unavailable: { tone: 'amber', label: 'нет бота на стенде' },
};

export function GuestChannelsCard() {
  const qc = useQueryClient();
  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: fetchChannels });
  const healthQ = useQuery({ queryKey: ['integrations-health'], queryFn: fetchIntegrationsHealth });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['channels'] });
    void qc.invalidateQueries({ queryKey: ['integrations-health'] });
  }

  const toggle = useMutation({
    mutationFn: ({ name, enabled }: { name: ChannelName; enabled: boolean }) =>
      setChannelEnabled(name, enabled),
    onSuccess: (_r, { name, enabled }) => {
      const title = CHANNELS.find((c) => c.name === name)?.title ?? name;
      toast.success(enabled ? `${title} снова предлагается гостям` : `${title} выключен`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось переключить канал'),
  });

  const revert = useMutation({
    mutationFn: (name: ChannelName) => deleteChannel(name),
    onSuccess: (_r, name) => {
      const title = CHANNELS.find((c) => c.name === name)?.title ?? name;
      toast.success(`${title}: вернули общий бот Smena`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось вернуть общий бот'),
  });

  const configured = new Map((channelsQ.data?.items ?? []).map((c) => [c.channel, c]));

  function onDisable(name: ChannelName, title: string): void {
    const ok = window.confirm(
      `Выключить ${title}?\n\nЭтот способ перестанет предлагаться гостям в приглашениях. ` +
        'Гости, которые уже пишут в этот канал, продолжат писать - их сообщения не теряются. ' +
        'Включить обратно можно здесь же.',
    );
    if (ok) toggle.mutate({ name, enabled: false });
  }

  function onRevert(name: ChannelName, title: string): void {
    const ok = window.confirm(
      `Вернуть общий бот Smena для ${title}?\n\nТокен вашего бота будет удалён, ` +
        'и гостям начнут выдаваться приглашения в общий бот. Восстановить токен можно только заново.',
    );
    if (ok) revert.mutate(name);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlugsConnectedIcon weight="bold" className="size-4 text-accent-fg" /> Каналы гостя
        </CardTitle>
        <div className="text-xs text-muted">
          Общий бот Smena работает по умолчанию - подключать его не нужно. Выключенный канал
          просто не предлагается гостю в приглашении.
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {channelsQ.isPending ? (
          <div className="text-sm text-muted">Загружаем настройки каналов</div>
        ) : (
          CHANNELS.map(({ name, title, hint }) => {
            const cfg = configured.get(name);
            const stand = readiness(healthQ.data?.channels, name);
            const bot = botOf(name, cfg, stand);
            const state = stateOf(name, cfg, stand);
            const chip = STATE_CHIP[state];
            const busy =
              (toggle.isPending && toggle.variables?.name === name) ||
              (revert.isPending && revert.variables === name);
            // Веб-чат - запасной путь: если выключить и его, у брони может не остаться
            // ни одного способа связи. Сервер это тоже не разрешает.
            const lockedOn = name === 'webchat';

            return (
              <div key={name} className="rounded-md border border-line bg-panel2/40 p-3">
                <div className="flex items-start gap-2">
                  {state === 'works' ? (
                    <CheckCircleIcon weight="fill" className="mt-0.5 size-4 shrink-0 text-green" />
                  ) : state === 'off' ? (
                    <ProhibitIcon weight="fill" className="mt-0.5 size-4 shrink-0 text-muted" />
                  ) : (
                    <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0 text-amber" />
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 text-sm font-medium">
                      {title}
                      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                      {cfg?.kind === 'custom' ? (
                        <span className="text-xs font-normal text-muted">ваш бот</span>
                      ) : null}
                      {lockedOn ? (
                        <span className="flex items-center gap-1 text-[11px] font-normal text-muted">
                          <LockSimpleIcon weight="bold" className="size-3" /> всегда включён
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-0.5 text-[11px] text-muted">
                      {state === 'off'
                        ? 'Выключен: гостям этот способ не предлагаем. Входящие сообщения по-прежнему доходят.'
                        : state === 'unavailable'
                          ? `Бот этого канала не поднят на стенде: ${stand?.detail ?? 'нет токена'}. Это настройка платформы, не ваша.`
                          : hint}
                    </div>

                    {bot ? (
                      <div className="mt-1 text-[11px] text-muted">
                        Гости пишут боту <span className="font-medium text-text">{bot.title}</span>
                        {bot.username ? <span> · @{bot.username}</span> : null}
                        {bot.url ? (
                          <>
                            {' · '}
                            <a
                              href={bot.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent-fg underline underline-offset-2"
                            >
                              {shortUrl(bot.url)}
                            </a>
                          </>
                        ) : null}
                      </div>
                    ) : null}

                    {cfg?.lastError ? (
                      <div className="mt-1 text-[11px] text-red">{cfg.lastError}</div>
                    ) : null}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {state === 'off' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => toggle.mutate({ name, enabled: true })}
                      >
                        {busy ? 'Включаем…' : 'Включить'}
                      </Button>
                    ) : !lockedOn && state === 'works' ? (
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onDisable(name, title)}>
                        {busy ? 'Выключаем…' : 'Выключить'}
                      </Button>
                    ) : null}

                    {cfg?.kind === 'custom' ? (
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRevert(name, title)}>
                        Вернуть общий бот
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
